/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

/**
 * Worktree Changes Operations
 * 
 * This module handles all worktree-related operations including:
 * - Getting worktree status and changed files
 * - Viewing commit history
 * - Getting file diffs
 * - Committing, pushing, and reverting changes
 * - Creating pull requests from worktree branches
 */

import type { SourceControlProvider } from '../../types';
import { getProductServerBaseUrl, buildProductApiUrl } from './base';
import type {
  WorktreeStatusResult,
  CommitHistoryResult,
  DiffResult,
  CommitResult,
  PushResult,
  RevertResult,
  RevertCommitFileResult,
  CreatePRResult,
  CommitFilesResult,
  CommitFileDiffResult,
} from '../../../server/types';

// ============================================================================
// Status Operations
// ============================================================================

/**
 * Get worktree status (changed files, branch info)
 * 
 * @param repositoryUrl - URL of the repository
 * @param worktreePath - Path to the worktree
 * @param repoPath - Optional path within monorepo to filter files
 * @returns Worktree status with changed files
 */
export async function getWorktreeStatus(
  repositoryUrl: string,
  worktreePath: string,
  repoPath?: string
): Promise<WorktreeStatusResult> {
  try {
    const apiUrl = buildProductApiUrl('/api/worktree-changes/status', {
      repositoryUrl,
      worktreePath,
      repoPath
    });

    const response = await fetch(apiUrl);

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to get worktree status (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error getting worktree status:', error);
    return {
      success: false,
      error: error.message || 'Failed to get worktree status'
    };
  }
}

/**
 * Get commit history for a worktree
 * 
 * @param repositoryUrl - URL of the repository
 * @param worktreePath - Path to the worktree
 * @param limit - Maximum number of commits to return (default 50)
 * @returns Commit history with push status
 */
export async function getCommitHistory(
  repositoryUrl: string,
  worktreePath: string,
  limit: number = 50
): Promise<CommitHistoryResult> {
  try {
    const apiUrl = buildProductApiUrl('/api/worktree-changes/history', {
      repositoryUrl,
      worktreePath,
      limit
    });

    const response = await fetch(apiUrl);

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to get commit history (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error getting commit history:', error);
    return {
      success: false,
      error: error.message || 'Failed to get commit history'
    };
  }
}

/**
 * Get diff for a specific file
 * 
 * @param repositoryUrl - URL of the repository
 * @param worktreePath - Path to the worktree
 * @param filePath - Path to the file within the worktree
 * @returns Diff content and file status
 */
export async function getFileDiff(
  repositoryUrl: string,
  worktreePath: string,
  filePath: string
): Promise<DiffResult> {
  try {
    const apiUrl = buildProductApiUrl('/api/worktree-changes/diff', {
      repositoryUrl,
      worktreePath,
      filePath
    });

    const response = await fetch(apiUrl);

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to get diff (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error getting file diff:', error);
    return {
      success: false,
      error: error.message || 'Failed to get file diff'
    };
  }
}

// ============================================================================
// Change Operations
// ============================================================================

/**
 * Commit changes in a worktree
 * 
 * @param repositoryUrl - URL of the repository
 * @param worktreePath - Path to the worktree
 * @param message - Commit message
 * @param files - Optional specific files to commit (all if not specified)
 * @returns Commit result with hash
 */
export async function commitChanges(
  repositoryUrl: string,
  worktreePath: string,
  message: string,
  files?: string[]
): Promise<CommitResult> {
  try {
    const apiUrl = `${getProductServerBaseUrl()}/api/worktree-changes/commit`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repositoryUrl,
        worktreePath,
        message,
        files
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.message || errorData.error || `Failed to commit changes (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error committing changes:', error);
    return {
      success: false,
      error: error.message || 'Failed to commit changes'
    };
  }
}

/**
 * Push commits from a worktree
 * 
 * @param repositoryUrl - URL of the repository
 * @param worktreePath - Path to the worktree
 * @param accessToken - Access token for authentication
 * @param provider - Source control provider ('github' or 'gitlab')
 * @param instanceUrl - Optional GitLab instance URL
 * @returns Push result
 */
export async function pushCommits(
  repositoryUrl: string,
  worktreePath: string,
  accessToken: string,
  provider: SourceControlProvider = 'github',
  instanceUrl?: string
): Promise<PushResult> {
  try {
    const apiUrl = `${getProductServerBaseUrl()}/api/worktree-changes/push`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repositoryUrl,
        worktreePath,
        accessToken,
        provider,
        instanceUrl
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.message || errorData.error || `Failed to push commits (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error pushing commits:', error);
    return {
      success: false,
      error: error.message || 'Failed to push commits'
    };
  }
}

/**
 * Revert changes in a worktree
 * 
 * @param repositoryUrl - URL of the repository
 * @param worktreePath - Path to the worktree
 * @param files - Optional specific files to revert (all if not specified)
 * @returns Revert result
 */
export async function revertChanges(
  repositoryUrl: string,
  worktreePath: string,
  files?: string[]
): Promise<RevertResult> {
  try {
    const apiUrl = `${getProductServerBaseUrl()}/api/worktree-changes/revert`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repositoryUrl,
        worktreePath,
        files
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.message || errorData.error || `Failed to revert changes (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error reverting changes:', error);
    return {
      success: false,
      error: error.message || 'Failed to revert changes'
    };
  }
}

/**
 * Revert a specific file's changes from a commit
 * 
 * @param repositoryUrl - URL of the repository
 * @param worktreePath - Path to the worktree
 * @param commitHash - Hash of the commit containing the file changes to revert
 * @param filePath - Path to the file within the worktree
 * @returns Revert result, including conflict information if applicable
 */
export async function revertCommitFile(
  repositoryUrl: string,
  worktreePath: string,
  commitHash: string,
  filePath: string
): Promise<RevertCommitFileResult> {
  try {
    const apiUrl = `${getProductServerBaseUrl()}/api/worktree-changes/revert-commit-file`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repositoryUrl,
        worktreePath,
        commitHash,
        filePath
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.message || errorData.error || `Failed to revert commit file (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error reverting commit file:', error);
    return {
      success: false,
      error: error.message || 'Failed to revert commit file'
    };
  }
}

/**
 * Get files changed in a specific commit
 * 
 * @param repositoryUrl - URL of the repository
 * @param worktreePath - Path to the worktree
 * @param commitHash - Hash of the commit to inspect
 * @returns List of files changed in the commit
 */
export async function getCommitFiles(
  repositoryUrl: string,
  worktreePath: string,
  commitHash: string
): Promise<CommitFilesResult> {
  try {
    const apiUrl = buildProductApiUrl('/api/worktree-changes/commit-files', {
      repositoryUrl,
      worktreePath,
      commitHash
    });

    const response = await fetch(apiUrl);

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to get commit files (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error getting commit files:', error);
    return {
      success: false,
      error: error.message || 'Failed to get commit files'
    };
  }
}

/**
 * Get diff for a file at a specific commit
 * 
 * @param repositoryUrl - URL of the repository
 * @param worktreePath - Path to the worktree
 * @param commitHash - Hash of the commit
 * @param filePath - Path to the file within the worktree
 * @returns Diff content for the file at the specific commit
 */
export async function getCommitFileDiff(
  repositoryUrl: string,
  worktreePath: string,
  commitHash: string,
  filePath: string
): Promise<CommitFileDiffResult> {
  try {
    const apiUrl = buildProductApiUrl('/api/worktree-changes/commit-file-diff', {
      repositoryUrl,
      worktreePath,
      commitHash,
      filePath
    });

    const response = await fetch(apiUrl);

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.error || `Failed to get commit file diff (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error getting commit file diff:', error);
    return {
      success: false,
      error: error.message || 'Failed to get commit file diff'
    };
  }
}

/**
 * Create a pull request from a worktree branch
 * 
 * @param repositoryUrl - URL of the repository
 * @param worktreePath - Path to the worktree
 * @param title - PR title
 * @param body - PR body/description
 * @param baseBranch - Base branch for the PR
 * @param accessToken - Access token for authentication
 * @param provider - Source control provider ('github' or 'gitlab')
 * @returns Create PR result with PR info
 */
export async function createPullRequest(
  repositoryUrl: string,
  worktreePath: string,
  title: string,
  body: string,
  baseBranch: string,
  accessToken: string,
  provider: SourceControlProvider = 'github'
): Promise<CreatePRResult> {
  try {
    const apiUrl = `${getProductServerBaseUrl()}/api/worktree-changes/create-pr`;

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        repositoryUrl,
        worktreePath,
        title,
        body,
        baseBranch,
        accessToken,
        provider
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      return {
        success: false,
        error: errorData.message || errorData.error || `Failed to create PR (${response.status})`
      };
    }

    return await response.json();
  } catch (error: any) {
    console.error('[Product API] Error creating pull request:', error);
    return {
      success: false,
      error: error.message || 'Failed to create pull request'
    };
  }
}

